/**
 * Scratch state that animation presets hang on Fabric objects while they run,
 * and the code that takes it back off again.
 *
 * Owns six kinds of borrowed state:
 *  - the clip rectangle that wipe/reveal presets animate
 *  - the overlay a layer draws in its own place while a preset shows pieces
 *    of it (split-up text, ticker copies, a reveal block)
 *  - the `objectCaching` flag that scale presets switch off for speed
 *  - the text a typewriter shortens while it types
 *  - the media child's original position, which Ken Burns replaces while
 *    it plays
 *  - a blur preset's blur, which works on the layer's finished picture
 *    through `renderEffects`; releasing an object takes the hook off with
 *    the rest
 *
 * All of it lives in WeakMaps keyed by the object, never on the layer, so
 * none of it is ever saved with the template.
 *
 * Two ways to give it back, and they are the only way this file is left:
 *  - `resetBorrowedState` puts the object back on its starting values but
 *    KEEPS what was remembered, so a later block on the same object still
 *    has something to animate. The canvas engine calls it when a seek lands
 *    between blocks.
 *  - `releaseBorrowedState` puts it back and FORGETS it, when a timeline is
 *    torn down.
 */
import {
  FabricObject,
  Rect,
  WebGLFilterBackend,
  config as fabricConfig,
  filters as fabricFilters,
  getFilterBackend
} from 'fabric';
import { mediaChildFitCount } from '@/helpers/imageFit';
import {
  CACHE_PADDING_PX,
  releaseRenderEffects,
  setPictureFilter
} from '@/services/animation/renderEffects';
import type { FabricText } from 'fabric';
import type {
  Animatable,
  AnimationOverlay,
  AnimTimeline,
  ClipHost,
  LayerBlur,
  LayerPicture,
  MediaChildPlacement,
  MediaChildStash,
  OverlayCopy,
  OverlayItem,
  OverlayRect
} from '@/types/layers/animationRuntime';

/** Blur presets' blur on each layer. */
const layerBlurs = new WeakMap<Animatable, LayerBlur>();

/**
 * How far Fabric's Blur filter reaches per unit of blur, as a share of the
 * short side of what it blurs (`Blur.getBlurValue`).
 */
const BLUR_REACH = 0.12;

/** Below this amount a blur changes nothing visible, so the layer draws as usual. */
const MIN_BLUR = 0.002;

// the blur canvas grows in these steps, so a slightly larger picture rarely
// means a new WebGL context
const BLUR_CANVAS_STEP = 256;

let ownBlurBackend: WebGLFilterBackend | null = null;
let ownBlurBackendFailed = false;

/** CSS pixels of blur per unit, for the plain preview tiles. */
const PREVIEW_BLUR_PX = 40;

/** Pre-tween objectCaching of objects the scale presets uncached. */
const stashedCaching = new WeakMap<Animatable, boolean>();

/**
 * Media-child base transforms stashed by kenBurns, keyed by the GROUP, with
 * the renderer's fit count for the child at the time.
 */
const stashedMediaBase = new WeakMap<Animatable, MediaChildStash>();

/** Overlays mounted by presets, keyed by the layer that draws them. */
const overlays = new WeakMap<Animatable, AnimationOverlay>();

/**
 * A typewriter's full text, kept while it shows only the start of it, and the
 * cut it showed last.
 */
const stashedText = new WeakMap<Animatable, { text: string; shown: string }>();

/** Each object's animation clip rectangle, shared by all its clip blocks. */
const animClips = new WeakMap<Animatable, Rect>();

/** The switch that hangs each object's clip rectangle on it or takes it off. */
const clipToggles = new WeakMap<Animatable, { on: number }>();

/*
 * Clip rectangles
 */

/**
 * The object's animation clip rectangle, hung on it only while a clip block
 * runs — from `at` for `duration` seconds — and, after an exit, from then on,
 * because the layer is meant to stay wiped away. The block tweens the
 * rectangle it returns.
 *
 * There is only ever ONE such rectangle per object, and every clip block on
 * that object animates it. A wipe-in followed by a wipe-out reuses the same
 * rectangle instead of making a new one, so both blocks keep driving the
 * pixels the canvas actually shows.
 *
 * The rest of the time nothing clips the layer, so strokes, italic letters
 * that overhang the text box and children reaching past a group's box show
 * as they do in the design. A delayed entrance that holds its start pose
 * draws the opening set too, so it waits fully clipped.
 *
 * If the object already has a clip of its own (a mask frame's rounded
 * corners, a masked child's inset), the rectangle hangs at the END of that
 * chain rather than replacing anything. Fabric clips a clip by its own clip,
 * so the visible result is the overlap of all of them and the design's masks
 * keep working for the whole animation.
 */
export function clipWhileRunning(
  tl: AnimTimeline,
  obj: Animatable,
  at: number,
  duration: number,
  keepAfter: boolean
): Rect {
  const clip = clipRectOf(obj);
  const toggle = clipToggleOf(obj, clip);
  tl.set(toggle, { on: 1, immediateRender: false }, at);
  if (!keepAfter) {
    tl.set(toggle, { on: 0, immediateRender: false }, at + duration);
  }
  return clip;
}

/**
 * Clip an object to a rectangle fixed on the canvas, so it can move through
 * it. Walks the clipPath chain so design masks are preserved, then disables
 * caching for the tween. Does nothing for plain preview objects.
 */
export function attachTickerClip(
  obj: Animatable,
  left: number,
  top: number,
  width: number,
  height: number
): void {
  if (typeof (obj as { set?: unknown }).set !== 'function') return;
  const clip = new Rect({
    left,
    top,
    width,
    height,
    absolutePositioned: true,
    stroke: null,
    strokeWidth: 0,
    shadow: null,
    objectCaching: false
  });
  appendToClipChain(obj, clip);
  disableCacheForScaleTween(obj);
}

/**
 * Mark the object — and every clip above the animation rectangle — as
 * needing a redraw next frame. Fabric caches clipped drawing, so without this
 * a clip tween would not show up.
 */
export function dirtyForClipTween(obj: Animatable): void {
  obj.dirty = true;
  let host = obj.clipPath as ClipHost | undefined;
  while (host && !isAnimClip(host)) {
    host.dirty = true;
    host = host.clipPath as ClipHost | undefined;
  }
}

/*
 * Overlays
 */

/**
 * The overlay `owner` draws in its own place, creating it on first use.
 *
 * Once mounted, the owner's drawing goes through the overlay: while its
 * `state.overlay` is on, the owner draws itself only if `state.self` is on,
 * then draws the overlay's items on top, all inside its own transform. So the
 * items move, scale, turn, clip and hide with the owner and everything above
 * it, and sit at its depth among the other layers. The owner's own clip is
 * applied last, over both.
 */
export function mountOverlay(owner: FabricObject): AnimationOverlay {
  const existing = overlays.get(owner);
  if (existing) return existing;

  const hadOwnDraw = Object.prototype.hasOwnProperty.call(owner, 'drawObject');
  const hadOwnShouldCache = Object.prototype.hasOwnProperty.call(
    owner,
    'shouldCache'
  );
  const hadOwnDims = Object.prototype.hasOwnProperty.call(
    owner,
    '_getCacheCanvasDimensions'
  );
  const draw = owner.drawObject;
  const shouldCache = owner.shouldCache;
  const getDims = owner._getCacheCanvasDimensions;
  const overlay: AnimationOverlay = {
    items: [],
    state: { self: 1, overlay: 0 },
    restore: () => {
      if (hadOwnDraw) owner.drawObject = draw;
      else delete (owner as Partial<FabricObject>).drawObject;
      if (hadOwnShouldCache) owner.shouldCache = shouldCache;
      else delete (owner as Partial<FabricObject>).shouldCache;
      if (hadOwnDims) owner._getCacheCanvasDimensions = getDims;
      else delete (owner as Partial<FabricObject>)._getCacheCanvasDimensions;
    }
  };

  // Any bitmap of the owner — a clip-forced cache, or a render effect's
  // picture of it — must cover the items too, which may reach past its box.
  owner._getCacheCanvasDimensions = function (this: FabricObject) {
    const dims = getDims.call(this);
    if (!overlay.state.overlay) return dims;
    const reach = overlayReach(overlay, this);
    const x = Math.max(dims.x, 2 * reach.x * dims.zoomX);
    const y = Math.max(dims.y, 2 * reach.y * dims.zoomY);
    return {
      ...dims,
      x,
      y,
      width: Math.ceil(x + CACHE_PADDING_PX),
      height: Math.ceil(y + CACHE_PADDING_PX)
    };
  };

  // While the items are drawn, no cache: they may reach past the owner's
  // box, and a cache would cut them off at its edge. A clip still forces
  // one, as Fabric requires. The rest of the time the owner caches as usual.
  owner.shouldCache = function (this: FabricObject): boolean {
    if (!overlay.state.overlay) return shouldCache.call(this);
    this.ownCaching = this.needsItsOwnCache();
    return this.ownCaching;
  };

  owner.drawObject = function (
    this: FabricObject,
    ...args: Parameters<FabricObject['drawObject']>
  ): void {
    const [ctx, forClipping, context] = args;
    if (forClipping || !overlay.state.overlay) {
      draw.call(this, ...args);
      return;
    }
    const clipPath = this.clipPath;
    this.clipPath = undefined;
    if (overlay.state.self) draw.call(this, ctx, false, context);
    for (const item of overlay.items) {
      if (item.clip && item.clipOn) {
        ctx.save();
        clipToRect(ctx, item.clip);
        drawItem(item, ctx, () => draw.call(this, ctx, false, context));
        ctx.restore();
      } else {
        drawItem(item, ctx, () => draw.call(this, ctx, false, context));
      }
    }
    this.clipPath = clipPath;
    // Fabric's own step for applying a clip.
    if (clipPath) this._drawClipPath(ctx, clipPath, context);
  };

  overlays.set(owner, overlay);
  return overlay;
}

/** Whether a preset has mounted an overlay on this object. */
export function hasOverlay(obj: Animatable): boolean {
  return overlays.has(obj);
}

/** Whether the overlay is currently drawing in place of the object. */
export function isOverlayActive(obj: Animatable): boolean {
  const overlay = overlays.get(obj);
  return !!overlay && overlay.state.overlay === 1;
}

/**
 * From clock second `at`, the layer draws its overlay in place of itself. Its
 * opacity goes back to `opacity` first: an exit earlier on the timeline may
 * have left it at 0, and the overlay is drawn through it.
 */
export function drawOverlayFrom(
  tl: AnimTimeline,
  owner: FabricObject,
  at: number,
  opacity: number
): void {
  const { state } = mountOverlay(owner);
  tl.set(owner, { opacity }, at);
  tl.set(state, { self: 0, overlay: 1 }, at);
}

/** From clock second `at`, the layer draws itself again, not its overlay. */
export function drawSelfFrom(
  tl: AnimTimeline,
  owner: FabricObject,
  at: number
): void {
  tl.set(mountOverlay(owner).state, { self: 1, overlay: 0 }, at);
}

/**
 * Add one object to the overlay `owner` draws, placed in the owner's plane.
 * Uncached, so it stays sharp at any zoom: it is never on a canvas that
 * could tell a cache how big to be.
 */
export function addOverlayItem(
  owner: FabricObject,
  obj: FabricObject
): OverlayItem {
  obj.objectCaching = false;
  const item: OverlayItem = { obj };
  mountOverlay(owner).items.push(item);
  return item;
}

/**
 * Add a copy of `owner` itself to its overlay, drawn exactly as the owner
 * draws — wrapped lines, borders, filters and all — moved by the copy's
 * `left`/`top` in the owner's plane. Tween the returned item's `copy`.
 */
export function addOverlayCopy(
  owner: FabricObject,
  copy: OverlayCopy
): OverlayItem {
  const item: OverlayItem = { copy };
  mountOverlay(owner).items.push(item);
  return item;
}

/*
 * Cache control and stashes
 */

/**
 * Turn off Fabric's per-object cache for a scale tween, remembering the old
 * setting.
 *
 * A cached object redraws and resizes its cache canvas on every tick of a
 * scale tween, the most expensive thing Fabric does per frame. Drawing
 * straight to the canvas is far cheaper.
 *
 * Does nothing for plain preview objects, which have no such flag. An object
 * with a clip keeps a cache regardless of this flag; the scale presets mark
 * it dirty each tick so that cache stays correct.
 */
export function disableCacheForScaleTween(obj: Animatable): void {
  if (obj.objectCaching === undefined || stashedCaching.has(obj)) return;
  stashedCaching.set(obj, obj.objectCaching);
  obj.objectCaching = false;
}

/** Remember a media child's original position and scale, once. */
export function stashMediaChildBase(obj: Animatable, child: Animatable): void {
  if (stashedMediaBase.has(obj)) return;
  stashedMediaBase.set(obj, { child, ...mediaChildPlacement(child) });
}

/**
 * Whether the renderer has placed the media child again since Ken Burns
 * remembered its position and scale: for a new file (a feed row, a URL swap),
 * a new box or a new fit mode. The remembered values belong to the old
 * placement, so the timeline must be rebuilt.
 */
export function mediaChildRefitted(obj: Animatable): boolean {
  const stash = stashedMediaBase.get(obj);
  return !!stash && mediaChildFitCount(stash.child) !== stash.fitCount;
}

/**
 * Remember where the renderer has placed the media child again, in place of
 * where Ken Burns found it. Called before a timeline is reverted: GSAP puts
 * back the values of the old placement, and the teardown then moves the
 * child to the new one.
 */
export function adoptMediaChildFit(obj: Animatable): void {
  const stash = stashedMediaBase.get(obj);
  if (!stash || !mediaChildRefitted(obj)) return;
  Object.assign(stash, mediaChildPlacement(stash.child));
}

/**
 * Show only the first `length` characters of `full`, remembering the text
 * the object had before the first cut.
 */
export function showTextPrefix(
  obj: FabricText,
  full: string,
  length: number
): void {
  const shown = full.slice(0, length);
  const stash = stashedText.get(obj);
  if (stash) stash.shown = shown;
  else stashedText.set(obj, { text: obj.text, shown });
  obj.set('text', shown);
  obj.dirty = true;
}

/**
 * Blur the whole layer by `amount`, any layer type: the blur works on its
 * finished picture, so text, shapes, groups and media all blur alike.
 *
 * The reach is measured the way Fabric's Blur filter measures it on an image:
 * from the short side of the media child for an image or video, so those look
 * as they always have, and from the layer's own box otherwise. Media stays
 * inside its frame; anything else spreads past its box. Preview tiles are
 * plain objects, so they get a CSS blur instead.
 */
export function setLayerBlur(
  obj: Animatable,
  amount: number,
  mediaChild?: Animatable
): void {
  if (!(obj instanceof FabricObject)) {
    obj.blurPx = amount * PREVIEW_BLUR_PX;
    return;
  }
  let blur = layerBlurs.get(obj);
  if (!blur) {
    blur = newLayerBlur(obj, mediaChild);
    layerBlurs.set(obj, blur);
    const state = blur;
    setPictureFilter(obj, 'blur', {
      isActive: () => state.amount > MIN_BLUR,
      apply: picture => blurPicture(picture, state)
    });
  }
  // a hold sets the same amount every frame; redrawing then would defeat the
  // reuse of last frame's blur
  if (blur.amount === amount) return;
  blur.amount = amount;
  obj.dirty = true;
}

/*
 * Giving it back
 */

/**
 * Put an object back on its starting values but KEEP what the presets
 * remembered, so a later block on it still has something to animate: the
 * animation clip opens to full size, the overlay steps aside for the object
 * itself, the full text and the media child's place come back, and the blur
 * clears. Does nothing for objects no preset touched.
 */
export function resetBorrowedState(obj: Animatable): void {
  resetAnimatedClip(obj);
  resetOverlay(obj);
  resetSlicedText(obj);
  resetMediaChild(obj);
  resetLayerBlur(obj);
}

/**
 * Hand back everything the presets borrowed and FORGET it: the animation
 * clip comes off, the overlay is unmounted, and the caching flag, the full
 * text and the media child's place are restored, and the blur comes off. Safe to run
 * twice; does nothing for objects no preset touched.
 */
export function releaseBorrowedState(obj: Animatable): void {
  clearAnimatedClip(obj);
  unmountOverlay(obj);
  restoreObjectCaching(obj);
  resetSlicedText(obj);
  stashedText.delete(obj);
  resetMediaChild(obj);
  stashedMediaBase.delete(obj);
  layerBlurs.delete(obj);
  releaseRenderEffects(obj);
}

/*
 * Helpers
 */

/** The object's animation clip rectangle, creating it on first use. */
function clipRectOf(obj: Animatable): Rect {
  const existing = animClips.get(obj);
  if (existing) return existing;
  const clip = new Rect({
    width: obj.width ?? 0,
    height: obj.height ?? 0,
    originX: 'center',
    originY: 'center',
    left: 0,
    top: 0,
    stroke: null,
    strokeWidth: 0,
    shadow: null
  });
  clip.data = { animClip: true };
  animClips.set(obj, clip);
  return clip;
}

/**
 * A number GSAP can tween that hangs the clip on (1) or takes it off (0).
 * GSAP writes it forward and restores it on the way back, so the clip follows
 * the playhead in either direction without relying on callbacks.
 */
function clipToggleOf(obj: Animatable, clip: Rect): { on: number } {
  const existing = clipToggles.get(obj);
  if (existing) return existing;
  const host = clipChainEnd(obj);
  const toggle = {
    get on() {
      return host.clipPath === clip ? 1 : 0;
    },
    set on(value: number) {
      host.clipPath = value ? clip : undefined;
      dirtyForClipTween(obj);
    }
  };
  clipToggles.set(obj, toggle);
  return toggle;
}

/**
 * Open the animation clip back to full size instead of removing it.
 *
 * A fully open, centered clip covers the whole object, so it looks the same
 * as having no clip — but keeping it attached means a later clip block on
 * the same object still has something to animate. Does nothing when there is
 * no animation clip.
 */
function resetAnimatedClip(obj: Animatable): void {
  const clip = animClipOf(obj);
  if (!clip) return;
  clip.set({ scaleX: 1, scaleY: 1, left: 0, top: 0 });
  dirtyForClipTween(obj);
}

/**
 * Remove the animation's clip rectangle, whether it sits on the object itself
 * or at the end of a chain of other clips. Clips the design owns are left
 * exactly where they were.
 */
function clearAnimatedClip(obj: Animatable): void {
  // The next timeline builds a fresh rectangle for the object's current size.
  animClips.delete(obj);
  clipToggles.delete(obj);
  const host = clipChainEnd(obj);
  if (!isAnimClip(host.clipPath)) return;
  host.clipPath = undefined;
  obj.dirty = true;
}

/**
 * Show the owner itself and none of its overlay, keeping the overlay for a
 * later block. Does nothing for objects without one.
 */
function resetOverlay(obj: Animatable): void {
  const overlay = overlays.get(obj);
  if (!overlay) return;
  overlay.state.self = 1;
  overlay.state.overlay = 0;
  obj.dirty = true;
}

/** Hand the owner's drawing back and forget its overlay. */
function unmountOverlay(obj: Animatable): void {
  const overlay = overlays.get(obj);
  if (!overlay) return;
  overlay.restore();
  overlays.delete(obj);
  obj.dirty = true;
}

/**
 * Give back the cache setting a scale preset switched off, and redraw once.
 * Does nothing for objects no preset touched.
 */
function restoreObjectCaching(obj: Animatable): void {
  const saved = stashedCaching.get(obj);
  if (saved === undefined) return;
  stashedCaching.delete(obj);
  obj.objectCaching = saved;
  obj.dirty = true;
}

/**
 * Put a typewriter's full text back, keeping it for a later block. Text that
 * something else wrote since the last cut (the canvas sync of a text edit or
 * of a new feed row) is newer than the remembered text, so it stays.
 */
function resetSlicedText(obj: Animatable): void {
  const stash = stashedText.get(obj);
  if (!stash) return;
  const textObj = obj as FabricText;
  if (textObj.text !== stash.shown) return;
  textObj.set('text', stash.text);
  stash.shown = stash.text;
  obj.dirty = true;
}

/**
 * Put the media child back where Ken Burns found it, keeping the remembered
 * values so a later block can reset to them too. Does nothing for untouched
 * objects.
 */
function resetMediaChild(obj: Animatable): void {
  const stash = stashedMediaBase.get(obj);
  if (!stash) return;
  stash.child.left = stash.left;
  stash.child.top = stash.top;
  stash.child.scaleX = stash.scaleX;
  stash.child.scaleY = stash.scaleY;
  stash.child.setCoords?.();
  obj.dirty = true;
}

/** Clear a layer's blur, keeping its hook for a later block. */
function resetLayerBlur(obj: Animatable): void {
  const blur = layerBlurs.get(obj);
  if (!blur) return;
  blur.amount = 0;
  blur.last = null;
  obj.dirty = true;
}

function newLayerBlur(obj: FabricObject, mediaChild?: Animatable): LayerBlur {
  const size = mediaChild
    ? Math.min(
        (mediaChild.width ?? 0) * Math.abs(mediaChild.scaleX ?? 1),
        (mediaChild.height ?? 0) * Math.abs(mediaChild.scaleY ?? 1)
      )
    : Math.sqrt(obj.width * obj.height);
  return {
    amount: 0,
    size,
    spread: !mediaChild,
    filter: new fabricFilters.Blur({ blur: 0 }),
    source: document.createElement('canvas'),
    target: document.createElement('canvas'),
    last: null
  };
}

/**
 * Blur a layer's picture with Fabric's own Blur filter and filter backend,
 * so it looks as image blur always has and falls back to 2D without WebGL.
 *
 * A spreading blur first pads the picture by its reach. Fabric measures the
 * reach from the short side of what it is handed, so the amount is rescaled
 * to the padded size. A picture larger than Fabric's texture limit is
 * blurred at a lower resolution, which the blur hides.
 */
function blurPicture(picture: LayerPicture, blur: LayerBlur): LayerPicture {
  const zoom = (picture.zoomX + picture.zoomY) / 2;
  const reach = BLUR_REACH * blur.amount * blur.size * zoom;
  const pad = blur.spread ? Math.ceil(reach) : 0;
  const scale = Math.min(
    1,
    fabricConfig.textureSize /
      (Math.max(picture.canvas.width, picture.canvas.height) + 2 * pad)
  );
  const width = Math.ceil((picture.canvas.width + 2 * pad) * scale);
  const height = Math.ceil((picture.canvas.height + 2 * pad) * scale);

  // the same picture at the same size and amount blurs to the same result
  const last = blur.last;
  if (
    picture.redrawn === false &&
    last !== null &&
    last.input === picture.canvas &&
    last.zoomX === picture.zoomX &&
    last.zoomY === picture.zoomY &&
    last.width === width &&
    last.height === height &&
    last.amount === blur.amount
  ) {
    return last.picture;
  }

  // The picture goes in as it is unless it needs padding or shrinking.
  let input = picture.canvas;
  if (pad > 0 || scale < 1) {
    input = blankCanvas(blur.source, width, height);
    input
      .getContext('2d')!
      .drawImage(
        picture.canvas,
        pad * scale,
        pad * scale,
        picture.canvas.width * scale,
        picture.canvas.height * scale
      );
  }
  const target = blankCanvas(blur.target, width, height);
  blur.filter.blur = (reach * scale) / (BLUR_REACH * Math.min(width, height));
  blurBackend(Math.max(width, height)).applyFilters(
    [blur.filter],
    input,
    width,
    height,
    target
  );
  const result: LayerPicture = {
    canvas: target,
    zoomX: picture.zoomX * scale,
    zoomY: picture.zoomY * scale,
    originX: (picture.originX + pad) * scale,
    originY: (picture.originY + pad) * scale
  };
  blur.last = {
    input: picture.canvas,
    zoomX: picture.zoomX,
    zoomY: picture.zoomY,
    width,
    height,
    amount: blur.amount,
    picture: result
  };
  return result;
}

/**
 * The filter backend layer blurs run on. Fabric's shared one draws into a
 * canvas `textureSize` square (4096 px), and copying each result out of it
 * copied that whole canvas: about 40 ms per blurred layer, against 2 ms from a
 * canvas the size of the picture. Layer blurs get their own WebGL canvas, only
 * as large as the biggest picture blurred so far. Without WebGL, or when a
 * second WebGL context cannot be made, they use Fabric's own backend as before.
 */
function blurBackend(size: number): ReturnType<typeof getFilterBackend> {
  const shared = getFilterBackend();
  if (!(shared instanceof WebGLFilterBackend) || ownBlurBackendFailed) {
    return shared;
  }
  const own = ownBlurBackend;
  if (own?.gl && !own.gl.isContextLost() && own.tileSize >= size) return own;

  if (own) {
    own.gl?.getExtension('WEBGL_lose_context')?.loseContext();
    own.dispose();
  }
  const tileSize = Math.min(
    fabricConfig.textureSize,
    Math.ceil(size / BLUR_CANVAS_STEP) * BLUR_CANVAS_STEP
  );
  ownBlurBackend = new WebGLFilterBackend({ tileSize });
  if (ownBlurBackend.gl) return ownBlurBackend;

  // no second WebGL context: stay on the shared one rather than retry each frame
  ownBlurBackend = null;
  ownBlurBackendFailed = true;
  return shared;
}

/** `canvas`, sized to `width × height` and cleared. */
function blankCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): HTMLCanvasElement {
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  } else {
    canvas.getContext('2d')!.clearRect(0, 0, width, height);
  }
  return canvas;
}

/**
 * Draw one overlay item in the owner's plane. `drawOwner` draws the owner
 * itself, which a copy repeats at its own offset and opacity.
 */
function drawItem(
  item: OverlayItem,
  ctx: CanvasRenderingContext2D,
  drawOwner: () => void
): void {
  if (item.shown === 0) return;
  if (item.obj) {
    item.obj.render(ctx);
    return;
  }
  const copy = item.copy;
  if (!copy || copy.opacity <= 0) return;
  ctx.save();
  ctx.globalAlpha *= copy.opacity;
  ctx.translate(copy.left, copy.top);
  drawOwner();
  ctx.restore();
}

/**
 * How far the overlay's shown items reach from the owner's centre, in the
 * owner's own units, never less than its own half box. An item object counts
 * by its centre plus its half diagonal, so a turned or skewed piece is still
 * covered; a copy counts as the owner's box moved by its offset.
 */
function overlayReach(
  overlay: AnimationOverlay,
  owner: FabricObject
): { x: number; y: number } {
  let x = owner.width / 2;
  let y = owner.height / 2;
  for (const item of overlay.items) {
    if (item.shown === 0) continue;
    if (item.obj) {
      const m = item.obj.calcTransformMatrix();
      const r =
        (Math.hypot(item.obj.width, item.obj.height) / 2) *
        Math.max(Math.abs(item.obj.scaleX), Math.abs(item.obj.scaleY));
      x = Math.max(x, Math.abs(m[4]) + r);
      y = Math.max(y, Math.abs(m[5]) + r);
    } else if (item.copy && item.copy.opacity > 0) {
      x = Math.max(x, Math.abs(item.copy.left) + owner.width / 2);
      y = Math.max(y, Math.abs(item.copy.top) + owner.height / 2);
    }
  }
  return { x, y };
}

function clipToRect(ctx: CanvasRenderingContext2D, rect: OverlayRect): void {
  ctx.beginPath();
  ctx.rect(rect.left, rect.top, rect.width, rect.height);
  ctx.clip();
}

/**
 * Hang a clip at the end of the object's clip chain, marked as the
 * animation's own so teardown can find it again.
 */
function appendToClipChain(obj: Animatable, clip: Rect): void {
  clip.data = { animClip: true };
  let host: ClipHost = obj;
  while (host.clipPath) host = host.clipPath as ClipHost;
  host.clipPath = clip;
}

/**
 * Where the animation rectangle hangs: the last clip the design itself put on
 * the object, or the object when it has none. Its `clipPath` is the
 * animation rectangle, or nothing.
 */
function clipChainEnd(obj: Animatable): ClipHost {
  let host: ClipHost = obj;
  while (host.clipPath && !isAnimClip(host.clipPath)) {
    host = host.clipPath as ClipHost;
  }
  return host;
}

/** Whether this clip object is the one the animation created. */
function isAnimClip(clip: unknown): boolean {
  const data = (clip as { data?: { animClip?: boolean } } | null | undefined)
    ?.data;
  return data?.animClip === true;
}

/**
 * Find the object's animation clip rectangle wherever it sits in the chain of
 * clips. Undefined when there is none.
 */
function animClipOf(obj: Animatable): Rect | undefined {
  const { clipPath } = clipChainEnd(obj);
  return isAnimClip(clipPath) ? (clipPath as Rect) : undefined;
}

/** Where the renderer has placed a media child, and how many times it has. */
function mediaChildPlacement(child: Animatable): MediaChildPlacement {
  return {
    fitCount: mediaChildFitCount(child),
    left: child.left ?? 0,
    top: child.top ?? 0,
    scaleX: child.scaleX ?? 1,
    scaleY: child.scaleY ?? 1
  };
}
